import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { formatToWinMultiple, shouldEnableToWinQuote } from './helpers';
import { cn } from '@/client/utils';
import { publicClient } from '@/client/utils/chain-client';
import { useBuyWin } from '@/libs/react-sdk/contracts/use-buy-win';
import { useOptions } from '@/libs/react-sdk/contracts/use-options';
import { TooltipArrow } from '@radix-ui/react-tooltip';
import { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { parseUnits } from 'viem';
import { Address } from 'viem/accounts';

type ToWinTooltipProps = PropsWithChildren & {
  className?: string;
  side?: 'right' | 'top' | 'bottom' | 'left' | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote: Pick<ReturnType<typeof useToWinQuote>, 'isLoading' | 'label'>;
};

type UseToWinQuoteProps = {
  enabled: boolean;
  marketAddress: string;
  marketDecimal: number;
  optionAddress: string;
  optionDecimal: number;
  apiOptionList?: Array<{ optionAddress: string }>;
};

export const useToWinQuote = ({
  enabled,
  marketAddress,
  marketDecimal,
  optionAddress,
  optionDecimal,
  apiOptionList,
}: UseToWinQuoteProps) => {
  const optionsList = useOptions(publicClient, marketAddress as Address, { enabled });

  // Fall back to API optionList when on-chain options() reverts
  const currentOptionsIndex = useMemo(() => {
    if (optionsList.data) {
      return optionsList.data.findIndex(item => item.toLowerCase() === optionAddress?.toLowerCase());
    }
    if (optionsList.isError && apiOptionList) {
      return apiOptionList.findIndex(item => item.optionAddress.toLowerCase() === optionAddress?.toLowerCase());
    }
    return -1;
  }, [optionAddress, optionsList.data, optionsList.isError, apiOptionList]);
  const quoteEnabled = shouldEnableToWinQuote(enabled, currentOptionsIndex);
  const buyParams = useMemo(() => {
    if (!marketAddress || !optionAddress || currentOptionsIndex < 0) return;
    return {
      address: marketAddress as Address,
      optionIndex: BigInt(currentOptionsIndex),
      tokenAmount: parseUnits('1', marketDecimal),
      deadline: BigInt(Math.ceil(Date.now() / 1e3) + 3 * 60),
      minOptionAmount: BigInt(0),
    };
  }, [currentOptionsIndex, marketAddress, marketDecimal, optionAddress]);
  const quote = useBuyWin(publicClient, buyParams, { enabled: quoteEnabled });
  const label = useMemo(
    () => formatToWinMultiple(quote.data ?? null, optionDecimal),
    [optionDecimal, quote.data]
  );

  return {
    ...quote,
    currentOptionsIndex,
    label,
  };
};

// Presentational only: the quote is owned by the caller (one useToWinQuote per
// button, shared between the button label and this tooltip) so the tooltip
// adds no query observers of its own.
export const ToWinTooltip = ({ children, className, side = 'top', open, onOpenChange, quote }: ToWinTooltipProps) => {
  const { t } = useTranslation();

  return (
    <Tooltip open={open} onOpenChange={onOpenChange}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side={side}
        className={cn(
          'inline-flex items-center gap-.5',
          'bg-gray-800 dark:bg-gray-700 text-white text-xs font-medium leading-5 p-2.5 !animate-none text-center max-w-fit break-words',
          className
        )}
      >
        <span>{t('to-win-label')}</span>
        <span>{quote.isLoading ? '...' : quote.label ?? '--'}</span>
        <TooltipArrow className="!animate-none fill-gray-800 dark:fill-gray-700" />
      </TooltipContent>
    </Tooltip>
  );
};
